import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Spin, Button, message, Switch, App, Row, Col } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import dayjs from 'dayjs';
import * as catalogueDetailService from '../services/api/catalogueDetail';
import ProductVariantsTable from '../components/Components/Tables/ProductVariantsTable';
import AddIcon from '@mui/icons-material/Add';
import { PrimaryButton, OutlineButton } from '../components/Components/Buttons/Buttons';
import BulkAddModal from '../components/ARSCatalog/BulkAddModal';
import { getUserCatalogues } from '../services/catalogueService';
import HeaderComponent from '../components/Components/Headers/HeaderComponent';
import { secureGetItem as getSecureItem } from '../data/utils';
import DownloadIcon from '@mui/icons-material/Download';
import DownloadCSVModal from '../components/ARSCatalog/DownloadCSVModal';
import { getCatalogueStats } from '../services/catalogueService';
import CustomTooltip from '../components/Components/Modals/CustomTooltip';
import ReorderModal from '../components/ARSCatalog/Modals/ReorderModal';
import ReorderIcon from '@mui/icons-material/Reorder';
import HoverImageZoom from '../components/Components/Images/HoverImageZoom';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDeleteModal from '../components/ARSCatalog/Modals/ConfirmDeleteModal';
import UnifiedProductDetail from '../components/ProductDetail/UnifiedProductDetail';
import { fetchCatalogueProductById, fetchCatalogueVariantsByProductRef } from '../services/api/ARSCatalogApi';
import { removeWhiteBackground } from '../services/utils/ARSCatalogUtils';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../data/base';
import CatalogueFilterBar from '../components/MyCatalogues/CatalogueFilterBar';
import { searchCatalogueProductsAndVariants } from '../services/api/catalogueDetailSearch';
import { findDocumentByField } from '../services/utils/csvGenerator';

const { Text } = Typography;

// Costanti per le larghezze delle colonne - Gestione centralizzata
const COLUMN_WIDTHS = {
    // Tabella Prodotti
    PRODUCT_IMAGE: '8%',
    PRODUCT_NAME: '22%',
    PRODUCT_VARIANTS: '7%',
    PRODUCT_PRIORITY: '7%',
    PRODUCT_BRAND: '22%',
    PRODUCT_LAST_UPDATE: '10%',
    PRODUCT_VISIBILITY: '10%',
    PRODUCT_DELETE: '8%',

    // Tabella Varianti
    VARIANT_IMAGE: '7%',
    VARIANT_SKU: '11%',
    VARIANT_EAN: '11%',
    VARIANT_PRIORITY: '7%',
    VARIANT_SIZE: '7%',
    VARIANT_FRAME_COLOR: '12%',
    VARIANT_LENSES_COLOR: '12%',
    VARIANT_ID: '11%',
    VARIANT_VISIBILITY: '9%',
    VARIANT_DELETE: '8%',
};

const CatalogueDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { message: messageApi } = App.useApp();

    const baseUrl = process.env.REACT_APP_ACTIVE_BASE_URL
        ? process.env.REACT_APP_ACTIVE_BASE_URL.replace(/^https?:\/\//, '')
        : 'arshadesstaging.web.app';

    const [viewerId, setViewerId] = useState(null);

    useEffect(() => {
        const findViewerId = async () => {
            try {
                const viewerDoc = await findDocumentByField('3DViewers', 'catalog', id);
                if (viewerDoc) {
                    setViewerId(viewerDoc.id); // Usa il doc.id del documento 3DViewers
                }
            } catch (error) {
                console.warn('Errore nel recuperare viewerId da 3DViewers, uso fallback:', error);
            }
        }

        findViewerId();
    }, [id, viewerId]);


    const [clientId, setClientId] = useState(null);
    const [userRole, setUserRole] = useState(null);

    // Funzione per determinare se l'utente può accedere alle azioni di modifica
    const canPerformActions = (role) => {
        const restrictedRoles = ['Modeller', 'ModellerSupervisor', 'Validator'];
        return !restrictedRoles.includes(role);
    };

    // Stato principale
    const [catalogue, setCatalogue] = useState(null);
    const [loading, setLoading] = useState(true);

    // Stati per i prodotti e le varianti
    const [products, setProducts] = useState([]);
    const [variants, setVariants] = useState({});
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingVariants, setLoadingVariants] = useState(false);
    const [processedImages, setProcessedImages] = useState({});

    // Stati per la paginazione e infinite scroll
    const [currentPage, setCurrentPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [lastVisible, setLastVisible] = useState(null); // Aggiungo stato per il cursore
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // Stati per l'espansione delle righe
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);



    // Stato per tenere traccia degli aggiornamenti di visibilità
    const [updatingVisibility, setUpdatingVisibility] = useState(false);

    // Stato per tracciare gli ID specifici in corso di aggiornamento
    const [updatingItems, setUpdatingItems] = useState(new Set());

    // Stato per il salvataggio del riordinamento
    const [isSavingOrder, setIsSavingOrder] = useState(false);

    // Stato per contenere tutti i prodotti per il riordino
    const [allProductsForReorder, setAllProductsForReorder] = useState([]);
    const [loadingAllProducts, setLoadingAllProducts] = useState(false);

    // Stato per il riordinamento delle varianti
    const [reorderingVariantsInfo, setReorderingVariantsInfo] = useState({ visible: false, product: null, variants: [] });

    // Stati per la modale di bulk upload
    const [bulkAddModalVisible, setBulkAddModalVisible] = useState(false);
    const [catalogues, setCatalogues] = useState([]);
    const [loadingCatalogues, setLoadingCatalogues] = useState(false);

    // Stati per la modale di download CSV
    const [downloadCSVModalVisible, setDownloadCSVModalVisible] = useState(false);

    // Stato per la modale di riordinamento
    const [reorderModalVisible, setReorderModalVisible] = useState(false);

    // Stati per le statistiche del catalogo
    const [catalogueStats, setCatalogueStats] = useState({ mainAssets: 0, brands: 0, sku: 0 });

    // Stati per la modale di eliminazione
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleteModalType, setDeleteModalType] = useState('product'); // 'product' or 'variant'
    const [deleteModalItemName, setDeleteModalItemName] = useState('');
    const [deleteModalItemId, setDeleteModalItemId] = useState('');
    const [deleteModalProductId, setDeleteModalProductId] = useState(''); // Per le varianti
    const [deletingItem, setDeletingItem] = useState(false);

    // Stati per gestire la visualizzazione del dettaglio prodotto
    const [showProductDetail, setShowProductDetail] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);

    // 🎯 STEP 1: Stati per i filtri (pattern identico a ARSCatalog)
    const [searchTerm, setSearchTerm] = useState(''); // Termine digitato
    const [isSearchMode, setIsSearchMode] = useState(false); // Se siamo in modalità ricerca server-side
    const [searchResults, setSearchResults] = useState([]); // Risultati completi della ricerca per paginazione

    // Stati per i conteggi (identici a ARSCatalog)
    const [originalTotalProducts, setOriginalTotalProducts] = useState(0);
    const [originalTotalSkus, setOriginalTotalSkus] = useState(0);
    const [isFiltersApplied, setIsFiltersApplied] = useState(false);

    // Nuovo stato per conteggio SKU di ricerca
    const [searchTotalSkus, setSearchTotalSkus] = useState(0);

    // 🎯 Funzione per caricare le varianti dal database (SEMPRE dal database, mai mock!)
    const fetchSingleProductVariants = useCallback(async (product) => {
        try {
            setLoadingVariants(true);

            // SEMPRE carica le varianti reali dal database
            const variantsByProduct = await catalogueDetailService.getProductVariants([product]);

            const sortedVariantsByProduct = {};
            Object.keys(variantsByProduct).forEach(productId => {
                let productVariants = variantsByProduct[productId];

                // 🎯 Se siamo in modalità ricerca E abbiamo varianti specifiche che hanno fatto match, filtrale
                if (isSearchMode && variants._searchMatchedVariants && variants._searchMatchedVariants[product.id]) {
                    const matchedVariantIds = variants._searchMatchedVariants[product.id];
                    productVariants = productVariants.filter(variant => matchedVariantIds.includes(variant.id));
                }

                // Ordina le varianti per priorità
                const sortedVariants = productVariants.sort((a, b) => {
                    const aPriority = a.priority || 0;
                    const bPriority = b.priority || 0;

                    if (aPriority === 0 && bPriority === 0) return 0;
                    if (aPriority === 0) return -1;
                    if (bPriority === 0) return 1;
                    return aPriority - bPriority;
                });

                sortedVariantsByProduct[productId] = sortedVariants;
            });

            setVariants(prev => ({ ...prev, ...sortedVariantsByProduct }));
        } catch (error) {
            console.error("Error loading variants for product:", error);
            message.error("Failed to load variants");
        } finally {
            setLoadingVariants(false);
        }
    }, [isSearchMode, variants]);

    // 🎯 Funzione per gestire l'espansione delle righe con UX CORRETTA
    const handleRowExpand = useCallback((expanded, record) => {
        if (expanded) {
            // ✅ PRIMA COSA: Apri IMMEDIATAMENTE il pannello
            setExpandedRowKeys(prev => [...prev, record.id]);

            // ✅ SECONDA COSA: Se non ci sono varianti, caricale in background
            if (!variants[record.id] || variants[record.id].length === 0) {
                // TRUE LAZY LOADING: carica le varianti in background mentre il pannello è già aperto
                fetchSingleProductVariants(record);
            }
        } else {
            // Riga collassata
            setExpandedRowKeys(prev => prev.filter(key => key !== record.id));
        }
    }, [variants, fetchSingleProductVariants]);

    useEffect(() => {
        const loadUserData = async () => {
            try {
                // Carica Client ID
                let storedClientId = getSecureItem('clientId');

                // Se non c'è clientId, prova a usare ref_cliente come fallback
                if (!storedClientId || storedClientId === 'null' || storedClientId === '') {
                    const refCliente = await getSecureItem('clientId');

                    if (refCliente && refCliente !== 'null' && refCliente !== '') {
                        storedClientId = refCliente;
                    }
                }

                if (storedClientId && storedClientId !== 'null' && storedClientId !== '') {
                    setClientId(storedClientId);
                } else {
                    console.warn('No valid client ID found in secure storage. Value:', storedClientId);
                    setClientId(null);
                }

                // Carica il ruolo utente
                const storedRole = getSecureItem('role');
                if (storedRole && storedRole !== 'null' && storedRole !== '') {
                    setUserRole(storedRole);
                } else {
                    console.warn('No valid role found in secure storage. Value:', storedRole);
                    setUserRole(null);
                }
            } catch (error) {
                console.error('Error loading user data:', error);
                setClientId(null);
                setUserRole(null);
            }
        };

        loadUserData();
    }, []);

    // Funzione per aggiornare la visibilità di un prodotto
    const handleUpdateProductVisibility = async (productId, isVisible) => {
        // Previeni clic multipli rapidi
        if (updatingItems.has(`product-${productId}`)) {
            return;
        }

        try {
            setUpdatingVisibility(true);
            setUpdatingItems(prev => new Set(prev).add(`product-${productId}`));

            // Prima esegui la chiamata al server
            await catalogueDetailService.updateProductVisibility(productId, isVisible);

            // Solo se la chiamata è andata a buon fine, aggiorna lo stato locale
            setProducts(prev =>
                prev.map(product =>
                    product.id === productId ? { ...product, isVisible } : product
                )
            );

            // Aggiorna lo stato locale delle varianti in base alla visibilità del prodotto
            if (variants[productId]) {
                setVariants(prev => ({
                    ...prev,
                    [productId]: prev[productId].map(variant => ({ ...variant, isVisible }))
                }));
            }

            messageApi.success('Product visibility updated successfully');

        } catch (error) {
            console.error("Error updating product visibility:", error);
            messageApi.error("Failed to update product visibility: " + (error.message || error));

            // Non aggiornare lo stato locale se c'è stato un errore
            // Lo switch tornerà automaticamente al valore precedente
        } finally {
            setUpdatingVisibility(false);
            setUpdatingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(`product-${productId}`);
                return newSet;
            });
        }
    };

    // Funzione per aggiornare la visibilità di una variante
    const handleUpdateVariantVisibility = async (variantId, isVisible, productId) => {
        // Previeni clic multipli rapidi
        if (updatingItems.has(`variant-${variantId}`)) {
            return;
        }

        try {
            setUpdatingVisibility(true);
            setUpdatingItems(prev => new Set(prev).add(`variant-${variantId}`));

            // Prima esegui la chiamata al server
            await catalogueDetailService.updateVariantVisibility(variantId, isVisible);

            // Solo se la chiamata è andata a buon fine, aggiorna lo stato locale
            setVariants(prev => ({
                ...prev,
                [productId]: prev[productId].map(variant =>
                    variant.id === variantId ? { ...variant, isVisible } : variant
                )
            }));

            messageApi.success('Variant visibility updated successfully');

        } catch (error) {
            console.error("Error updating variant visibility:", error);
            messageApi.error("Failed to update variant visibility: " + (error.message || error));

            // Non aggiornare lo stato locale se c'è stato un errore
            // Lo switch tornerà automaticamente al valore precedente
        } finally {
            setUpdatingVisibility(false);
            setUpdatingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(`variant-${variantId}`);
                return newSet;
            });
        }
    };

    // Funzione per aprire la modale di eliminazione prodotto
    const showDeleteProductModal = (product) => {
        setDeleteModalType('product');
        setDeleteModalItemName(product.modelName || 'Untitled');
        setDeleteModalItemId(product.id);
        setDeleteModalProductId('');
        setDeleteModalVisible(true);
    };

    // Funzione per aprire la modale di eliminazione variante
    const showDeleteVariantModal = (variant, productId) => {
        setDeleteModalType('variant');
        setDeleteModalItemName(variant.skuModel || variant.id || 'Untitled');
        setDeleteModalItemId(variant.id);
        setDeleteModalProductId(productId);
        setDeleteModalVisible(true);
    };

    // Funzione per chiudere la modale di eliminazione
    const handleDeleteModalCancel = () => {
        if (!deletingItem) {
            setDeleteModalVisible(false);
            setDeleteModalType('product');
            setDeleteModalItemName('');
            setDeleteModalItemId('');
            setDeleteModalProductId('');
        }
    };

    // Funzione per confermare l'eliminazione
    const handleDeleteConfirm = async () => {
        try {
            setDeletingItem(true);

            if (deleteModalType === 'product') {
                // Elimina prodotto e tutte le sue varianti
                const result = await catalogueDetailService.deleteProductFromCatalogue(deleteModalItemId);

                messageApi.success({
                    content: `Product deleted successfully! Also deleted ${result.deletedVariants} variants.`,
                    duration: 3,
                });

                // Ricarica i prodotti
                await fetchInitialProducts(); // Use fetchInitialProducts for lazy loading
            } else {
                // Elimina solo la variante
                await catalogueDetailService.deleteVariantFromCatalogue(deleteModalItemId, deleteModalProductId);

                messageApi.success({
                    content: 'Variant deleted successfully!',
                    duration: 2,
                });

                // Ricarica le varianti per il prodotto specifico
                const productToUpdate = products.find(p => p.id === deleteModalProductId);
                if (productToUpdate) {
                    await fetchProductVariants([productToUpdate]);
                }
            }

        } catch (error) {
            console.error("Error deleting item:", error);
            messageApi.error({
                content: `Failed to delete ${deleteModalType}. Please try again.`,
                duration: 4,
            });
        } finally {
            setDeletingItem(false);
            setDeleteModalVisible(false);
            setDeleteModalType('product');
            setDeleteModalItemName('');
            setDeleteModalItemId('');
            setDeleteModalProductId('');
        }
    };

    // Funzione per gestire il click su un prodotto
    const handleProductClick = (productId) => {
        // Mostra il dettaglio del prodotto
        setSelectedProductId(productId);
        setShowProductDetail(true);
    };

    // Funzione per tornare indietro dal dettaglio prodotto
    const handleBackFromDetail = () => {
        setShowProductDetail(false);
        setSelectedProductId(null);
    };

    // Definizione delle colonne per la tabella dei prodotti - Updated alignment
    const columns = useMemo(() => [
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Image</span>
                    <CustomTooltip title="Image of the product">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'imgUrl',
            key: 'imgUrl',
            render: (text, record) => {
                const imgSrc = processedImages[record.id] || text;

                if (!processedImages[record.id] && text) {
                    removeWhiteBackground(text, record.id, processedImages, setProcessedImages);
                }

                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        {imgSrc ? (
                            <HoverImageZoom
                                src={imgSrc}
                                alt="Product"
                                style={{
                                    width: '3.5rem',
                                    height: '3.5rem',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '3.5rem',
                                height: '3.5rem',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: '#999',
                                fontSize: '0.75rem'
                            }}>
                                No Image
                            </div>
                        )}
                    </div>
                );
            },
            align: 'center',
            width: COLUMN_WIDTHS.PRODUCT_IMAGE,
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Product Name</span>
                    <CustomTooltip title="Name of the product">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'modelName',
            key: 'modelName',
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => handleProductClick(record.id)}
                    style={{ padding: 0, textAlign: 'left' }}
                >
                    {text}
                </Button>
            ),
            sorter: (a, b) => {
                const aValue = (a.modelName || '').toLowerCase();
                const bValue = (b.modelName || '').toLowerCase();
                return aValue.localeCompare(bValue);
            },
            align: 'left',
            width: COLUMN_WIDTHS.PRODUCT_NAME,
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span>Variants</span>
                    <CustomTooltip title="Number of available variants">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'list_variants',
            key: 'variantsCount',
            render: (list_variants, record) => {
                // Usa la nuova mappa catalogues_variants_map se disponibile
                const count = record.catalogues_variants_map
                    ? record.catalogues_variants_map.length
                    : (list_variants ? list_variants.length : 0);
                return <div style={{ paddingRight: '0.5rem' }}>{count}</div>;
            },
            sorter: (a, b) => {
                // Ordina per lunghezza dell'array catalogues_variants_map o list_variants
                const aValue = a.catalogues_variants_map
                    ? a.catalogues_variants_map.length
                    : (a.list_variants ? a.list_variants.length : 0);
                const bValue = b.catalogues_variants_map
                    ? b.catalogues_variants_map.length
                    : (b.list_variants ? b.list_variants.length : 0);
                return aValue - bValue;
            },
            align: 'right',
            width: COLUMN_WIDTHS.PRODUCT_VARIANTS,
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span>Priority</span>
                    <CustomTooltip title="Display priority (0 = no priority set; 1+ = custom order)">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'priority',
            key: 'priority',
            render: (priority) => (
                <div style={{ paddingRight: '0.5rem' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '2rem',
                        height: '2rem',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '0.25rem',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        color: '#333',
                        marginLeft: 'auto'
                    }}>
                        {priority || 0}
                    </div>
                </div>
            ),
            sorter: (a, b) => {
                const aValue = a.priority || 0;
                const bValue = b.priority || 0;
                return aValue - bValue;
            },
            align: 'right',
            width: COLUMN_WIDTHS.PRODUCT_PRIORITY,
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Brand</span>
                    <CustomTooltip title="Associated brand">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'brandName',
            key: 'brandName',
            sorter: (a, b) => {
                const aValue = (a.brandName || '').toLowerCase();
                const bValue = (b.brandName || '').toLowerCase();
                return aValue.localeCompare(bValue);
            },
            align: 'left',
            width: COLUMN_WIDTHS.PRODUCT_BRAND,
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Last Update</span>
                    <CustomTooltip title="Date of last update">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'lastUpdate',
            key: 'lastUpdate',
            render: (date) => date ? dayjs(date.toDate ? date.toDate() : date).format('DD/MM/YYYY HH:mm') : '-',
            sorter: (a, b) => {
                const aValue = a.lastUpdate ? dayjs(a.lastUpdate.toDate ? a.lastUpdate.toDate() : a.lastUpdate).valueOf() : 0;
                const bValue = b.lastUpdate ? dayjs(b.lastUpdate.toDate ? b.lastUpdate.toDate() : b.lastUpdate).valueOf() : 0;
                return aValue - bValue;
            },
            align: 'left',
            width: COLUMN_WIDTHS.PRODUCT_LAST_UPDATE,
        },
        // Colonna Visibility - solo se l'utente può eseguire azioni
        ...(canPerformActions(userRole) ? [{
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Visibility</span>
                    <CustomTooltip title="Toggle product visibility">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'isVisible',
            key: 'visibility',
            render: (_, product) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <Switch
                            checked={product.isVisible !== false} // Default to true if not specified
                            onChange={(checked) => handleUpdateProductVisibility(product.id, checked)}
                            loading={updatingItems.has(`product-${product.id}`)}
                        />
                    </div>
                );
            },
            sorter: (a, b) => {
                const aValue = a.isVisible !== false ? 1 : 0;
                const bValue = b.isVisible !== false ? 1 : 0;
                return bValue - aValue; // Visible items first
            },
            align: 'center',
            width: COLUMN_WIDTHS.PRODUCT_VISIBILITY,
            fixed: 'right'
        }] : []),
        // Colonna Delete - solo se l'utente può eseguire azioni
        ...(canPerformActions(userRole) ? [{
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Delete</span>
                    <CustomTooltip title="Delete product and all its variants">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'delete',
            render: (_, product) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <Button
                            type="text"
                            danger
                            icon={<DeleteIcon />}
                            onClick={() => showDeleteProductModal(product)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Delete product"
                        />
                    </div>
                );
            },
            align: 'center',
            width: COLUMN_WIDTHS.PRODUCT_DELETE,
            fixed: 'right'
        }] : []),
    ], [id, updatingVisibility, handleUpdateProductVisibility, CustomTooltip, variants, userRole, showDeleteProductModal, handleProductClick]);


    // Definizione personalizzata delle colonne delle varianti
    const variantColumns = useMemo(() => {
        const columns = [
            // 1) Image
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Image</span>
                        <CustomTooltip title="Image of the variant">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'poster',
                key: 'poster',
                render: (text, variant) => {
                    const variantImgKey = `variant_${variant.id}`;
                    const imgSrc = processedImages[variantImgKey] || text;

                    if (!processedImages[variantImgKey] && text) {
                        removeWhiteBackground(text, variantImgKey, processedImages, setProcessedImages);
                    }

                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                            {imgSrc ? (
                                <HoverImageZoom
                                    src={imgSrc}
                                    alt="Variant"
                                    style={{
                                        width: '3.5rem',
                                        height: '3.5rem',
                                        objectFit: 'contain',
                                        display: 'block'
                                    }}
                                />
                            ) : (
                                <div style={{
                                    width: '3.5rem',
                                    height: '3.5rem',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: '#999',
                                    fontSize: '0.75rem'
                                }}>
                                    No Image
                                </div>
                            )}
                        </div>
                    );
                },
                align: 'center',
                width: COLUMN_WIDTHS.VARIANT_IMAGE,
            },
            // 2) SKU
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span>SKU</span>
                        <CustomTooltip title="SKU of the model">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'skuModel',
                key: 'skuModel',
                sorter: (a, b) => {
                    const aValue = (a.skuModel || '').toLowerCase();
                    const bValue = (b.skuModel || '').toLowerCase();
                    return aValue.localeCompare(bValue);
                },
                align: 'left',
                width: COLUMN_WIDTHS.VARIANT_SKU,
            },
            // 3) EAN
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span>EAN</span>
                        <CustomTooltip title="EAN code of the variant">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'eanCode',
                key: 'eanCode',
                render: (text) => text || '-',
                sorter: (a, b) => {
                    const aValue = (a.eanCode || '').toLowerCase();
                    const bValue = (b.eanCode || '').toLowerCase();
                    return aValue.localeCompare(bValue);
                },
                align: 'left',
                width: COLUMN_WIDTHS.VARIANT_EAN,
            },
            // 4) Priority
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span>Priority</span>
                        <CustomTooltip title="Display priority (0 = no priority set; 1+ = custom order)">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'priority',
                key: 'priority',
                render: (priority) => (
                    <div style={{ paddingRight: '0.5rem' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '2rem',
                            height: '2rem',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '0.25rem',
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            color: '#333',
                            marginLeft: 'auto'
                        }}>
                            {priority || 0}
                        </div>
                    </div>
                ),
                sorter: (a, b) => {
                    const aValue = a.priority || 0;
                    const bValue = b.priority || 0;
                    return aValue - bValue;
                },
                align: 'right',
                width: COLUMN_WIDTHS.VARIANT_PRIORITY,
            },
            // 5) Size
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span>Size</span>
                        <CustomTooltip title="Size of the variant">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'size',
                key: 'size',
                render: (size) => {
                    const sizeValue = size?.size || '-';
                    return <div style={{ paddingRight: '0.5rem' }}>{sizeValue}</div>;
                },
                sorter: (a, b) => {
                    const aValue = a.size?.size || '';
                    const bValue = b.size?.size || '';

                    // Prova a convertire in numero per l'ordinamento numerico
                    const aNum = parseFloat(aValue);
                    const bNum = parseFloat(bValue);

                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return aNum - bNum;
                    }

                    // Se non sono numeri, ordina come stringhe
                    return aValue.toString().toLowerCase().localeCompare(bValue.toString().toLowerCase());
                },
                align: 'right',
                width: COLUMN_WIDTHS.VARIANT_SIZE,
            },
            // 6) Frame Color
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span>Frame Color</span>
                        <CustomTooltip title="Color of the frame">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'frameColor',
                key: 'frameColor',
                sorter: (a, b) => {
                    const aValue = (a.frameColor || '').toLowerCase();
                    const bValue = (b.frameColor || '').toLowerCase();
                    return aValue.localeCompare(bValue);
                },
                align: 'left',
                width: COLUMN_WIDTHS.VARIANT_FRAME_COLOR,
            },
            // 7) Lenses Color
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span>Lenses Color</span>
                        <CustomTooltip title="Color of the lenses">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'lensesColor',
                key: 'lensesColor',
                render: (text) => text === "0" || !text ? "TRANSPARENT" : text,
                sorter: (a, b) => {
                    const aValue = (a.lensesColor === "0" || !a.lensesColor ? "TRANSPARENT" : a.lensesColor).toLowerCase();
                    const bValue = (b.lensesColor === "0" || !b.lensesColor ? "TRANSPARENT" : b.lensesColor).toLowerCase();
                    return aValue.localeCompare(bValue);
                },
                align: 'left',
                width: COLUMN_WIDTHS.VARIANT_LENSES_COLOR,
            },
        ];

        // Aggiungi la colonna ID alle varianti (solo per Admin)
        if (userRole === 'Admin') {
            columns.push({
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span>ID</span>
                        <CustomTooltip title="Variant ID">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'id',
                key: 'id',
                render: (text) => (
                    <Text style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                        {text}
                    </Text>
                ),
                sorter: (a, b) => {
                    const aValue = (a.id || '').toLowerCase();
                    const bValue = (b.id || '').toLowerCase();
                    return aValue.localeCompare(bValue);
                },
                align: 'left',
                width: COLUMN_WIDTHS.VARIANT_ID,
            });
        }

        // Aggiungi la colonna di visibilità alle varianti - solo se l'utente può eseguire azioni
        if (canPerformActions(userRole)) {
            columns.push({
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Visibility</span>
                        <CustomTooltip title="Toggle variant visibility">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'isVisible',
                key: 'visibility',
                render: (_, variant) => (
                    <Switch
                        checked={variant.isVisible !== false} // Default to true if not specified
                        onChange={(checked) => handleUpdateVariantVisibility(variant.id, checked, variant.productId)}
                        loading={updatingItems.has(`variant-${variant.id}`)}
                        disabled={!products.find(p => p.id === variant.productId)?.isVisible} // Disable if parent product is not visible
                    />
                ),
                sorter: (a, b) => {
                    const aValue = a.isVisible !== false ? 1 : 0;
                    const bValue = b.isVisible !== false ? 1 : 0;
                    return bValue - aValue; // Visible items first
                },
                align: 'center',
                width: COLUMN_WIDTHS.VARIANT_VISIBILITY,
                fixed: 'right'
            });

            // Aggiungi la colonna di eliminazione alle varianti
            columns.push({
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Delete</span>
                        <CustomTooltip title="Delete variant">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'delete',
                render: (_, variant) => (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <Button
                            type="text"
                            danger
                            icon={<DeleteIcon />}
                            onClick={() => showDeleteVariantModal(variant, variant.productId)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Delete variant"
                        />
                    </div>
                ),
                align: 'center',
                width: COLUMN_WIDTHS.VARIANT_DELETE,
                fixed: 'right'
            });
        }

        return columns;
    }, [updatingVisibility, products, handleUpdateVariantVisibility, CustomTooltip, userRole, showDeleteVariantModal]);

    // Caricamento dei dati iniziali
    useEffect(() => {
        const fetchCatalogueDetails = async () => {
            try {
                setLoading(true);
                if (id) {
                    // Recupera il catalogo dal database
                    const catalogueData = await catalogueDetailService.getCatalogueDetails(id);

                    if (catalogueData) {
                        setCatalogue(catalogueData);
                    } else {
                        message.error("Catalogue not found");
                        navigate('/my-catalogues');
                    }
                }
            } catch (error) {
                console.error("Error retrieving catalogue details:", error);
                message.error("An error occurred while loading catalogue details");
            } finally {
                setLoading(false);
            }
        };

        fetchCatalogueDetails();
    }, [id, navigate]);

    // Funzione per caricare le varianti per i prodotti
    const fetchProductVariants = useCallback(async (productsData) => {
        try {
            setLoadingVariants(true);
            const variantsByProduct = await catalogueDetailService.getProductVariants(productsData);

            // Ordina le varianti per priorità per ogni prodotto
            const sortedVariantsByProduct = {};
            Object.keys(variantsByProduct).forEach(productId => {
                const variants = variantsByProduct[productId];
                const sortedVariants = variants.sort((a, b) => {
                    const aPriority = a.priority || 0;
                    const bPriority = b.priority || 0;

                    // Se entrambi hanno priorità 0, mantieni l'ordine originale
                    if (aPriority === 0 && bPriority === 0) {
                        return 0;
                    }

                    // Se solo uno ha priorità 0, mettilo all'inizio
                    if (aPriority === 0) return -1;
                    if (bPriority === 0) return 1;

                    // Altrimenti ordina per priorità crescente
                    return aPriority - bPriority;
                });

                sortedVariantsByProduct[productId] = sortedVariants;
            });

            setVariants(prev => ({ ...prev, ...sortedVariantsByProduct }));
        } catch (error) {
            console.error("Error loading variants for products:", error);
            message.error("Failed to load variants");
        } finally {
            setLoadingVariants(false);
        }
    }, []);

    const PAGE_SIZE = 20; // Definisci la dimensione della pagina

    // Funzione per caricare i prodotti con paginazione
    const fetchInitialProducts = useCallback(async () => {
        if (!catalogue) return;

        setLoadingProducts(true);
        setProducts([]);
        setCurrentPage(1);
        setLastVisible(null);
        setHasMore(true);

        try {
            const result = await catalogueDetailService.getCatalogueProducts(id, 1, PAGE_SIZE, null, window.applyAdultFilter);
            setTotalProducts(result.totalCount);
            setLastVisible(result.lastVisible);

            if (result.products && result.products.length > 0) {
                const sortedProducts = result.products.sort((a, b) => (a.priority || 0) - (b.priority || 0));
                setProducts(sortedProducts);
                setHasMore(result.products.length === PAGE_SIZE);
                // 🎯 NON CARICARE PIÙ LE VARIANTI IN BACKGROUND - LAZY LOADING ON DEMAND
                // fetchProductVariants(sortedProducts);
            } else {
                setProducts([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error loading initial catalogue products:", error);
            message.error("Failed to load products");
            setProducts([]);
            setHasMore(false);
        } finally {
            setLoadingProducts(false);
        }
    }, [id, catalogue]);


    const loadMoreProducts = useCallback(async () => {
        if (loadingMore || !hasMore) return;

        setLoadingMore(true);

        try {
            if (isSearchMode) {
                // 🎯 MODALITÀ RICERCA: Carica più prodotti dai risultati salvati
                const currentLength = products.length;
                const nextProducts = searchResults.slice(currentLength, currentLength + PAGE_SIZE);

                if (nextProducts.length > 0) {
                    let newProductsToAdd = [];

                    // 🎯 PREVIENI DUPLICATI: Filtra prodotti già presenti usando currentLength come riferimento
                    setProducts(prev => {
                        const existingIds = new Set(prev.map(p => p.id));
                        newProductsToAdd = nextProducts.filter(p => !existingIds.has(p.id));
                        return [...prev, ...newProductsToAdd];
                    });

                    setHasMore(currentLength + nextProducts.length < searchResults.length);

                    // 🎯 In modalità ricerca, le varianti dovrebbero già essere disponibili
                    // Non è necessario caricarle nuovamente dal database
                } else {
                    setHasMore(false);
                }
            } else {
                // 🎯 MODALITÀ NORMALE: Carica dalla API
                if (!catalogue) return;

                const nextPage = currentPage + 1;
                const result = await catalogueDetailService.getCatalogueProducts(id, nextPage, PAGE_SIZE, lastVisible, window.applyAdultFilter);
                setHasMore(result.products.length === PAGE_SIZE);
                setLastVisible(result.lastVisible);

                const sortedProducts = result.products.sort((a, b) => (a.priority || 0) - (b.priority || 0));
                setProducts(prev => [...prev, ...sortedProducts]);
                setCurrentPage(nextPage);

                // 🎯 NON CARICARE PIÙ LE VARIANTI AUTOMATICAMENTE - LAZY LOADING ON DEMAND
                // if (sortedProducts.length > 0) {
                //     await fetchProductVariants(sortedProducts);
                // }
            }
        } catch (error) {
            console.error("Error loading more catalogue products:", error);
            message.error("Failed to load more products");
        } finally {
            setLoadingMore(false);
        }
    }, [loadingMore, hasMore, isSearchMode, products.length, searchResults, catalogue, currentPage, lastVisible, id]);

    // Carica i prodotti iniziali quando il catalogo è disponibile
    useEffect(() => {
        if (catalogue) {
            fetchInitialProducts();
        }
    }, [catalogue, fetchInitialProducts]);

    // Rimosso useEffect per paginazione e handleTableChange

    // State to track if we need to reset the stepper when reopening
    const [resetBulkAddStepper, setResetBulkAddStepper] = useState(false);

    // Function to open the bulk upload modal
    const showBulkAddModal = () => {
        setBulkAddModalVisible(true);
        // Reset the stepper when reopening
        setResetBulkAddStepper(true);
    };

    // Function to open the download CSV modal
    const showDownloadCSVModal = () => {
        setDownloadCSVModalVisible(true);
    };

    // Function to close the download CSV modal
    const handleDownloadCSVModalCancel = () => {
        setDownloadCSVModalVisible(false);
    };

    // Function to handle the modal close - let BulkAddModal handle its own confirmation
    const handleBulkAddModalCancel = () => {
        setBulkAddModalVisible(false);
        // Set flag to reset stepper on next open
        setResetBulkAddStepper(true);
    };

    // Function to handle direct close without confirmation
    const handleBulkAddModalDirectClose = () => {
        setBulkAddModalVisible(false);
        setResetBulkAddStepper(true);
    };

    // Funzione per recuperare i cataloghi
    const fetchCatalogues = async () => {
        try {
            setLoadingCatalogues(true);
            const userCatalogues = await getUserCatalogues();

            // Filtriamo solo i cataloghi pubblicati
            const publishedCatalogues = userCatalogues.filter(catalogue => !catalogue.isDraft);

            setCatalogues(publishedCatalogues);
        } catch (error) {
            console.error("Error fetching catalogues:", error);
            messageApi.error("Errore nel recupero dei cataloghi");
        } finally {
            setLoadingCatalogues(false);
        }
    };

    // Carica i cataloghi quando la modale viene aperta
    useEffect(() => {
        if (bulkAddModalVisible) {
            fetchCatalogues();
        }
    }, [bulkAddModalVisible]);

    // Function to get client ID when needed
    const getClientId = async () => {
        try {
            let storedClientId = await getSecureItem('clientId');

            // Se non c'è clientId, prova a usare ref_cliente come fallback
            if (!storedClientId || storedClientId === 'null' || storedClientId === '') {
                const refCliente = await getSecureItem('ref_cliente');

                if (refCliente && refCliente !== 'null' && refCliente !== '') {
                    storedClientId = refCliente;
                }
            }

            return storedClientId;
        } catch (error) {
            console.error('Error getting client ID:', error);
            return null;
        }
    };

    // Carica le statistiche del catalogo
    useEffect(() => {
        const loadCatalogueStats = async () => {
            if (catalogue) {
                try {
                    const stats = await getCatalogueStats(catalogue.id);
                    setCatalogueStats(stats);
                } catch (error) {
                    console.error("Error loading catalogue stats:", error);
                }
            }
        };

        loadCatalogueStats();
    }, [catalogue]);

    // Carica i totali originali all'avvio (usa i dati dal catalogo)
    useEffect(() => {
        const loadOriginalTotals = async () => {
            try {
                if (catalogueStats.mainAssets && catalogueStats.sku) {
                    setOriginalTotalProducts(catalogueStats.mainAssets);
                    setOriginalTotalSkus(catalogueStats.sku);
                }
            } catch (error) {
                console.error('Error loading original totals:', error);
            }
        };

        // Carica i totali originali quando abbiamo i dati del catalogo
        if (catalogueStats.mainAssets > 0 && originalTotalProducts === 0) {
            loadOriginalTotals();
        }
    }, [catalogueStats, originalTotalProducts]);

    // 🎯 STEP 1: Funzioni per i filtri (pattern identico a ARSCatalog)

    // Funzione per gestire il cambio del termine di ricerca (identica a ARSCatalog)
    const handleSearchTermChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);

        // Se il termine è vuoto, esci dalla modalità ricerca (come in ARSCatalog)
        if (!newSearchTerm || newSearchTerm.trim().length === 0) {
            setIsSearchMode(false);
            setSearchResults([]); // Pulisci i risultati di ricerca
            setSearchTotalSkus(0); // Reset conteggio SKU di ricerca
            // 🎯 COLLASSA TUTTE LE RIGHE ESPANSE
            setExpandedRowKeys([]);
            // 🎯 PULISCI LE VARIANTI DI RICERCA
            setVariants(prev => {
                const { _searchMatchedVariants, ...rest } = prev;
                return rest;
            });
        }
    };

    // Funzione per eseguire ricerca server-side (identica a ARSCatalog)
    const performServerSearch = async () => {
        if (!searchTerm || searchTerm.trim().length === 0) {
            setIsSearchMode(false);
            return;
        }

        setLoadingProducts(true); // Usa lo spinner principale
        setIsSearchMode(true);
        setProducts([]); // Svuota i risultati precedenti
        setSearchResults([]); // Pulisci anche i risultati salvati per evitare duplicati
        setVariants({}); // Pulisci le varianti
        // 🎯 COLLASSA TUTTE LE RIGHE ESPANSE QUANDO SI CERCA
        setExpandedRowKeys([]);

        try {
            const results = await searchCatalogueProductsAndVariants(id, searchTerm);

            if (results.products && results.products.length > 0) {
                // 🎯 SALVA I RISULTATI COMPLETI per la paginazione
                setSearchResults(results.products);

                // 🎯 SALVA IL CONTEGGIO TOTALE DELLE SKU DAI RISULTATI DI RICERCA
                setSearchTotalSkus(results.totalSkus || 0);

                // 🎯 SALVA LE VARIANTI SPECIFICHE CHE HANNO FATTO MATCH (per filtrarle all'expand)
                setVariants(prev => ({ ...prev, _searchMatchedVariants: results.matchedVariants || {} }));

                // 🎯 PAGINAZIONE: Prendi solo i primi PAGE_SIZE prodotti come nel caricamento normale
                const paginatedProducts = results.products.slice(0, PAGE_SIZE);
                setProducts(paginatedProducts);
                setTotalProducts(results.products.length); // Totale trovato dalla ricerca
                setHasMore(results.products.length > PAGE_SIZE); // Se ci sono più prodotti da caricare
            } else {
                setProducts([]);
                setVariants({});
                setTotalProducts(0);
                setSearchResults([]);
                setSearchTotalSkus(0);
                messageApi.info('No products found for your search.');
            }
        } catch (error) {
            console.error('Error in server-side search:', error);
            messageApi.error('Failed to search products');
            setProducts([]);
            setVariants({});
        } finally {
            setLoadingProducts(false); // Disattiva lo spinner principale
        }
    };

    // Funzione per applicare i filtri (identica a ARSCatalog)
    const handleApplyFilters = async () => {
        // Se c'è un searchTerm, fai ricerca server-side (come in ARSCatalog)
        if (searchTerm && searchTerm.trim().length > 0) {
            await performServerSearch();
            setIsFiltersApplied(true); // Marca i filtri come applicati
            return;
        }

        // Se non c'è ricerca, ricarica i prodotti normali
        setIsSearchMode(false);
        setIsFiltersApplied(false); // Reset filtri applicati
        setSearchTotalSkus(0); // Reset conteggio SKU di ricerca
        // 🎯 COLLASSA TUTTE LE RIGHE ESPANSE
        setExpandedRowKeys([]);
        // 🎯 PULISCI LE VARIANTI DI RICERCA
        setVariants(prev => {
            const { _searchMatchedVariants, ...rest } = prev;
            return rest;
        });
        await fetchInitialProducts();
    };

    // Funzione per pulire i filtri (identica a ARSCatalog)
    const handleClearFilters = async () => {
        setSearchTerm('');
        setIsSearchMode(false);
        setIsFiltersApplied(false); // Reset filtri applicati
        setSearchResults([]); // Pulisci i risultati di ricerca
        setSearchTotalSkus(0); // Reset conteggio SKU di ricerca
        // 🎯 COLLASSA TUTTE LE RIGHE ESPANSE QUANDO SI RESETTANO I FILTRI
        setExpandedRowKeys([]);
        // 🎯 PULISCI LE VARIANTI DI RICERCA
        setVariants(prev => {
            const { _searchMatchedVariants, ...rest } = prev;
            return rest;
        });
        // Ricarica i prodotti originali
        await fetchInitialProducts();
    };



    // Calcolo semplificato delle SKU per evitare loop infiniti
    const hybridFilteredSkus = isSearchMode ? searchTotalSkus : originalTotalSkus;

    // Funzione per caricare tutti i prodotti per il riordino
    const fetchAllProductsForReordering = async () => {
        if (!id) return;

        setLoadingAllProducts(true);
        messageApi.open({
            key: 'loading-all-products',
            type: 'loading',
            content: 'Loading all products for reordering...',
            duration: 0,
        });

        try {
            const allProductsData = await catalogueDetailService.getAllProductsForReordering(id);
            setAllProductsForReorder(allProductsData);
            setReorderModalVisible(true); // Apre la modale solo dopo aver caricato i prodotti
        } catch (error) {
            console.error("Error fetching all products for reordering:", error);
            messageApi.error("Failed to load products for reordering.");
        } finally {
            messageApi.destroy('loading-all-products');
            setLoadingAllProducts(false);
        }
    };

    // Funzione per aprire la modale di riordinamento dei prodotti
    const showProductReorderModal = () => {
        // Avvia il caricamento di tutti i prodotti
        fetchAllProductsForReordering();
    };

    // Funzione per chiudere la modale di riordinamento
    const handleReorderModalCancel = () => {
        setReorderModalVisible(false);
        setReorderingVariantsInfo({ visible: false, product: null, variants: [] });
        setAllProductsForReorder([]); // Pulisce lo stato quando si chiude
    };

    // Funzione per salvare il nuovo ordine dei prodotti
    const handleProductReorderSave = async (reorderedItems) => {
        setIsSavingOrder(true);

        try {
            const changesCount = await catalogueDetailService.saveProductsOrder(reorderedItems);

            messageApi.success({
                content: `Order saved successfully! Updated ${changesCount} products.`,
                duration: 2,
            });

            // Ricarica i prodotti per riflettere il nuovo ordine
            await fetchInitialProducts(); // Use fetchInitialProducts for lazy loading
        } catch (error) {
            console.error("Error saving new order:", error);
            messageApi.error({
                content: 'Failed to save the new order. Please try again.',
                duration: 4,
            });
        } finally {
            setIsSavingOrder(false);
            setReorderModalVisible(false);
            setAllProductsForReorder([]); // Pulisce lo stato dopo il salvataggio
        }
    };

    // Funzione per aprire la modale di riordinamento delle varianti
    const showVariantReorderModal = (product) => {
        const productVariants = variants[product.id] || [];
        setReorderingVariantsInfo({
            visible: true,
            product: product,
            variants: productVariants,
        });
    };

    // Funzione per salvare il nuovo ordine delle varianti
    const handleVariantReorderSave = async (reorderedItems) => {
        const { product } = reorderingVariantsInfo;
        if (!product) return;

        setIsSavingOrder(true);

        try {
            const changesCount = await catalogueDetailService.saveVariantsOrder(reorderedItems);

            messageApi.success({
                content: `Variants order saved successfully! Updated ${changesCount} variants.`,
                duration: 2,
            });
            // Ricarica le varianti per questo prodotto
            await fetchProductVariants(products);
        } catch (error) {
            console.error("Error saving new variant order:", error);
            messageApi.error({
                content: 'Failed to save the new variant order. Please try again.',
                duration: 4,
            });
        } finally {
            setIsSavingOrder(false);
            setReorderingVariantsInfo({ visible: false, product: null, variants: [] });
        }
    };

    // Mappatura degli item per la modale di riordino
    const reorderModalItems = useMemo(() => {
        if (reorderingVariantsInfo.visible) {
            // Adatta le varianti per la modale
            return reorderingVariantsInfo.variants.map(variant => ({
                id: variant.id,
                modelName: variant.skuModel || variant.id,
                brandName: variant.frameColor, // Usa un campo rilevante, es. colore
                imgUrl: variant.poster,
                priority: variant.priority || 0,
            }));
        }
        // Altrimenti, usa tutti i prodotti caricati per il riordino
        return allProductsForReorder;
    }, [reorderingVariantsInfo, allProductsForReorder]);

    // Funzione da passare al pulsante "Save" della modale
    const onReorderSave = reorderingVariantsInfo.visible
        ? handleVariantReorderSave
        : handleProductReorderSave;

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!catalogue) {
        return null;
    }

    // Componente per il dettaglio prodotto locale
    const CatalogueProductDetailLocal = ({ productId, onBack }) => {
        const apiConfig = {
            fetchProduct: fetchCatalogueProductById,
            fetchVariants: fetchCatalogueVariantsByProductRef,
            buildModelUrl: (product, variant, productId) => {
                // Costruisci l'URL usando gli ID originali
                const originalProductId = productId || null;
                const originalVariantId = variant.id || null;
                return `https://${baseUrl}/Arshades3d/${viewerId}/glasses/${originalProductId}/variant/${originalVariantId}?fov=15&fovMobile=32`;
            },
            buildVtoUrl: async (product, variant, productId, catalogueId) => {
                if (!product?.mainBrandRef) {
                    return (
                        <div style={{
                            width: '100%',
                            height: 'calc(100vh - 13.5rem)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '1.2rem'
                        }}>
                            Impossible to load the Virtual Try-On: mainBrandRef not available
                        </div>
                    );
                }

                // Usa catalogueId se disponibile, altrimenti mainBrandRef come fallback
                const effectiveCatalogueId = catalogueId || product.mainBrandRef;

                // Recupera il webDomain dalla collezione Catalogues
                let webDomain = 'staging.webvto.it'; // Fallback di default

                if (effectiveCatalogueId) {
                    try {
                        const catalogueRef = doc(db, "Catalogues", effectiveCatalogueId);
                        const catalogueSnap = await getDoc(catalogueRef);
                        if (catalogueSnap.exists()) {
                            const catalogueData = catalogueSnap.data();
                            if (catalogueData.webDomain) {
                                webDomain = catalogueData.webDomain;
                            }
                        }
                    } catch (error) {
                        console.warn('Errore nel recuperare webDomain dal catalogo, uso fallback:', error);
                    }
                }

                const brandId = product.mainBrandRef;
                const iframeSrc = `https://${webDomain}/?c=${brandId}&m=${variant.id}&debug=true`;

                return (
                    <iframe
                        src={iframeSrc}
                        title="Virtual Try-On"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            overflow: 'hidden',
                            display: 'block'
                        }}
                        scrolling="no"
                        frameBorder="0"
                        allowFullScreen
                        allow="camera"
                    />
                );
            }
        };

        const routeConfig = {
            backRoute: '/my-catalogues',
            catalogueRoute: '/my-catalogues',
            onBack: onBack // Passa la callback personalizzata
        };

        return (
            <UnifiedProductDetail
                apiConfig={apiConfig}
                routeConfig={routeConfig}
                componentName="Product"
                productId={productId}
                catalogueId={id}
                show3DAssets={false}
            />
        );
    };

    // Se è selezionato un prodotto, mostra il dettaglio
    if (showProductDetail && selectedProductId) {
        return (
            <CatalogueProductDetailLocal
                productId={selectedProductId}
                onBack={handleBackFromDetail}
            />
        );
    }

    return (
        <div>
            <HeaderComponent
                title={
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem' }}>
                        <span>{catalogue.nameCatalog}</span>
                        {(totalProducts > 0 || isFiltersApplied || isSearchMode) && (
                            <Typography.Text style={{ fontSize: '0.9rem', color: '#666', fontWeight: 'normal' }}>
                                {isFiltersApplied || isSearchMode ? (
                                    `Viewing ${totalProducts || 0} products (${hybridFilteredSkus} SKUs) out of ${originalTotalProducts || 0} products (${originalTotalSkus || 0} SKUs)`
                                ) : (
                                    `Viewing ${totalProducts || 0} products (${hybridFilteredSkus} SKUs)`
                                )}
                            </Typography.Text>
                        )}
                    </div>
                }
                buttonIcon={<NavigateBeforeIcon style={{ fontSize: '1.5rem' }} />}
                buttonText=""
                onButtonClick={() => navigate('/my-catalogues')}
                isVisible={true}
                buttonPosition="left"
                rightContent={
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <PrimaryButton icon={<DownloadIcon />} onClick={showDownloadCSVModal}>
                            Download CSV
                        </PrimaryButton>
                        {/* Mostra bottoni di modifica solo se l'utente può eseguire azioni */}
                        {canPerformActions(userRole) && (
                            <>
                                <PrimaryButton icon={<AddIcon />} onClick={showBulkAddModal}>
                                    Bulk Add 3D Assets
                                </PrimaryButton>
                                <OutlineButton
                                    icon={<ReorderIcon />}
                                    onClick={showProductReorderModal}
                                    loading={loadingAllProducts} // Mostra il caricamento sul pulsante
                                >
                                    Rearrange By Priority
                                </OutlineButton>
                            </>
                        )}
                    </div>
                }
                useSpaceBetween={true}
            />

            {/* 🎯 NUOVO: Layout a due colonne con filtri (identico a ARSCatalog) */}
            <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
                {/* Colonna dei filtri (30% dello spazio) */}
                <Col xs={24} sm={24} md={8} lg={7} xl={6}>
                    <CatalogueFilterBar
                        // 🎯 STEP 1: Props per la ricerca collegate
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchTermChange}
                        onApplyFilters={handleApplyFilters}
                        onClearFilters={handleClearFilters}

                        // Props mock per gli altri filtri - li collegheremo step by step
                        userRole={userRole}
                    />
                </Col>

                {/* Colonna della tabella (70% dello spazio) */}
                <Col xs={24} sm={24} md={16} lg={17} xl={18}>
                    {loadingProducts ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: 'white' }}>
                            <Spin size="large" />
                        </div>
                    ) : products.length > 0 ? (
                        <ProductVariantsTable
                            columns={columns}
                            products={products.filter(product =>
                                product.status === 'Pubblicato' || product.status === 'Published'
                            )}
                            loading={loadingProducts}
                            loadingMore={loadingMore}
                            loadingVariants={loadingVariants}
                            hasMore={hasMore}
                            handleLoadMore={loadMoreProducts}
                            expandedRowKeys={expandedRowKeys}
                            onRowExpand={handleRowExpand}
                            variants={variants}
                            variantColumns={variantColumns}
                            renderExpandedRowActions={(product) => {
                                const productVariants = variants[product.id] || [];
                                const hasMultipleVariants = productVariants.length > 1;

                                // Mostra il bottone solo se l'utente può eseguire azioni e ci sono multiple varianti
                                return hasMultipleVariants && canPerformActions(userRole) ? (
                                    <OutlineButton
                                        icon={<ReorderIcon />}
                                        onClick={() => showVariantReorderModal(product)}
                                    >
                                        Rearrange Variants By Priority
                                    </OutlineButton>
                                ) : null;
                            }}
                        />
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <Text type="secondary">{isSearchMode ? 'No results found for your search' : 'No products in this catalogue'}</Text>
                        </div>
                    )}
                </Col>
            </Row>

            <BulkAddModal
                visible={bulkAddModalVisible}
                onCancel={handleBulkAddModalCancel}
                onDirectClose={handleBulkAddModalDirectClose}
                catalogues={catalogues}
                loading={loadingCatalogues}
                initialCatalogId={id} // Pass the current catalog ID
                resetStepper={resetBulkAddStepper}
                onResetComplete={() => setResetBulkAddStepper(false)}
                clientId={clientId}
                getClientId={getClientId}
            />

            <ReorderModal
                key={reorderingVariantsInfo.visible ? `variants-${reorderingVariantsInfo.product?.id}` : 'products'}
                visible={reorderModalVisible || reorderingVariantsInfo.visible}
                onCancel={handleReorderModalCancel}
                onSave={onReorderSave}
                items={reorderModalItems}
                loading={isSavingOrder}
                mode={reorderingVariantsInfo.visible ? 'variants' : 'products'}
            />

            <DownloadCSVModal
                visible={downloadCSVModalVisible}
                onCancel={handleDownloadCSVModalCancel}
                catalogueId={id}
                catalogueName={catalogue?.nameCatalog || 'Catalogo'}
                skuCount={catalogueStats.sku}
            />

            <ConfirmDeleteModal
                isOpen={deleteModalVisible}
                onClose={handleDeleteModalCancel}
                onConfirm={handleDeleteConfirm}
                type={deleteModalType}
                itemName={deleteModalItemName}
                loading={deletingItem}
            />
        </div>
    );
};

export default CatalogueDetail;